import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Mail, Key, ArrowLeft, Check, X, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { API_URL } from '../../config';

export default function SecuritySettings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Passwort ändern States
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    emailCode: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [codeRequested, setCodeRequested] = useState(false);
  const [requestingCode, setRequestingCode] = useState(false);

  // 2FA States
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disableForm, setDisableForm] = useState({ password: '', code: '' });
  const [disableError, setDisableError] = useState('');

  // E-Mail-Code beim Login States
  const [emailCodeEnabled, setEmailCodeEnabled] = useState(false);
  const [emailCodeLoading, setEmailCodeLoading] = useState(false);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/user/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Nicht authentifiziert');

      const data = await res.json();
      setUser(data);
      setTwoFactorEnabled(data.two_factor_enabled || false);
      setEmailCodeEnabled(data.email_code_enabled || false);
    } catch (error) {
      console.error('Fehler beim Laden der User-Daten:', error);
      toast.error('Fehler beim Laden der Einstellungen');
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  // ===== PASSWORT ÄNDERN =====
  const requestPasswordChangeCode = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('Bitte fülle alle Passwort-Felder aus');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Neue Passwörter stimmen nicht überein');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordError('Neues Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setRequestingCode(true);
    setPasswordError('');

    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/auth/request-password-change-code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Code-Anforderung fehlgeschlagen');
      }

      setCodeRequested(true);
      toast.success('Bestätigungscode wurde an deine E-Mail gesendet');
    } catch (error) {
      setPasswordError(error.message);
      toast.error('Fehler beim Senden des Codes');
    } finally {
      setRequestingCode(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!passwordForm.emailCode || passwordForm.emailCode.length !== 6) {
      setPasswordError('Bitte gib den 6-stelligen Code ein');
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');

    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword,
          code: passwordForm.emailCode
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Passwort-Änderung fehlgeschlagen');
      }

      toast.success('✅ Passwort erfolgreich geändert! Du wirst ausgeloggt...');
      
      // SOFORTIGER Logout (alle Sessions ungültig)
      localStorage.clear();
      window.location.href = '/welcome';

    } catch (error) {
      setPasswordError(error.message);
      toast.error('Fehler beim Ändern des Passworts');
    } finally {
      setPasswordLoading(false);
    }
  };

  // ===== 2FA =====
  const handle2FAToggle = async () => {
    if (twoFactorEnabled) {
      // Deaktivieren - Dialog öffnen
      setShowDisableDialog(true);
      setDisableForm({ password: '', code: '' });
      setDisableError('');
    } else {
      // Aktivieren - Setup starten
      // SCHUTZ: Kein erneutes init wenn Setup bereits läuft
      if (showSetup) {
        toast.error('2FA-Setup läuft bereits');
        return;
      }
      
      setTwoFactorLoading(true);

      try {
        const token = localStorage.getItem('snapshot_token');
        const res = await fetch(`${API_URL}/api/auth/2fa/init`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!res.ok) throw new Error('2FA-Init fehlgeschlagen');

        const data = await res.json();
        setQrCode(data.qr_code);
        setSecret(data.secret);
        setShowSetup(true);
      } catch (error) {
        toast.error('Fehler beim Starten des 2FA-Setups');
      } finally {
        setTwoFactorLoading(false);
      }
    }
  };

  const confirm2FADisable = async () => {
    if (!disableForm.password || !disableForm.code) {
      setDisableError('Bitte fülle beide Felder aus');
      return;
    }

    setTwoFactorLoading(true);
    setDisableError('');

    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/auth/2fa/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          password: disableForm.password, 
          code: disableForm.code 
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || '2FA-Deaktivierung fehlgeschlagen');
      }

      setTwoFactorEnabled(false);
      setShowDisableDialog(false);
      toast.success('2FA erfolgreich deaktiviert');
      loadUserData();
    } catch (error) {
      setDisableError(error.message);
      toast.error(error.message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const verify2FASetup = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Bitte gib einen 6-stelligen Code ein');
      return;
    }

    setTwoFactorLoading(true);

    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/auth/2fa/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ verification_code: verifyCode })  // Backend erwartet verification_code
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Code-Verifikation fehlgeschlagen');
      }

      const data = await res.json();
      setBackupCodes(data.backup_codes || []);
      setTwoFactorEnabled(true);
      toast.success('✅ 2FA erfolgreich aktiviert!');
      
      // KEIN Auto-Close - User muss Backup-Codes bestätigen

    } catch (error) {
      toast.error(error.message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  // ===== E-MAIL-LOGIN-CODE =====
  const handleEmailCodeToggle = async () => {
    setEmailCodeLoading(true);

    try {
      const token = localStorage.getItem('snapshot_token');
      const newState = !emailCodeEnabled;

      // Optimistic Update - sofortiges UI-Feedback
      setEmailCodeEnabled(newState);

      // Aktualisiere User-Einstellung im Backend
      const res = await fetch(`${API_URL}/api/user/update-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email_code_enabled: newState
        })
      });

      if (!res.ok) {
        const data = await res.json();
        // Rollback bei Fehler
        setEmailCodeEnabled(!newState);
        throw new Error(data.detail || 'Einstellungs-Update fehlgeschlagen');
      }

      toast.success(
        newState 
          ? '✅ E-Mail-Code beim Login aktiviert' 
          : 'E-Mail-Code beim Login deaktiviert'
      );
      loadUserData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setEmailCodeLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 text-zinc-400 hover:text-white"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>

          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-emerald-500" />
            <h1 className="text-2xl md:text-3xl font-bold">Sicherheitseinstellungen</h1>
          </div>
          <p className="text-zinc-400">
            Verwalte deine Sicherheitsoptionen und Authentifizierungsmethoden
          </p>
        </div>

        <div className="space-y-6">
          {/* 1. PASSWORT ÄNDERN */}
          <Card className="bg-zinc-900 border-zinc-800" data-testid="password-change-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-emerald-500" />
                Passwort ändern
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Ändere dein Passwort. Alle aktiven Sessions werden beendet.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                {passwordError && (
                  <Alert variant="destructive" className="bg-red-950/30 border-red-500/50">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{passwordError}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="current-password">Aktuelles Passwort</Label>
                  <Input
                    id="current-password"
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                    className="bg-zinc-950 border-zinc-700"
                    disabled={codeRequested}
                    data-testid="current-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-password">Neues Passwort</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                    className="bg-zinc-950 border-zinc-700"
                    disabled={codeRequested}
                    data-testid="new-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Neues Passwort bestätigen</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                    className="bg-zinc-950 border-zinc-700"
                    disabled={codeRequested}
                    data-testid="confirm-password-input"
                  />
                </div>

                {!codeRequested ? (
                  <Button
                    type="button"
                    onClick={requestPasswordChangeCode}
                    disabled={requestingCode}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="request-code-button"
                  >
                    {requestingCode ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sende Code...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Bestätigungscode anfordern
                      </>
                    )}
                  </Button>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="email-code">Bestätigungscode (aus E-Mail)</Label>
                      <Input
                        id="email-code"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={passwordForm.emailCode}
                        onChange={(e) => setPasswordForm({...passwordForm, emailCode: e.target.value.replace(/\D/g, '')})}
                        className="bg-zinc-950 border-zinc-700 text-center text-lg tracking-widest"
                        placeholder="000000"
                        autoFocus
                        data-testid="email-code-input"
                      />
                      <p className="text-xs text-zinc-500">
                        Code ist 10 Minuten gültig
                      </p>
                    </div>

                    <Button
                      type="submit"
                      disabled={passwordLoading || passwordForm.emailCode.length !== 6}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="change-password-button"
                    >
                      {passwordLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Ändere Passwort...
                        </>
                      ) : (
                        'Passwort ändern'
                      )}
                    </Button>
                  </>
                )}
              </form>
            </CardContent>
          </Card>

          {/* 2. TWO-FACTOR AUTH */}
          <Card className="bg-zinc-900 border-zinc-800" data-testid="2fa-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-emerald-500" />
                Zwei-Faktor-Authentifizierung (2FA)
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Schütze deinen Account mit einem zusätzlichen Sicherheitscode beim Login
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">
                    Status: {twoFactorEnabled ? (
                      <span className="text-emerald-500">An</span>
                    ) : (
                      <span className="text-zinc-400">Aus</span>
                    )}
                  </p>
                  <p className="text-sm text-zinc-500">
                    {twoFactorEnabled ? 'Du benötigst einen 2FA-Code beim Login' : 'Aktiviere 2FA für zusätzliche Sicherheit'}
                  </p>
                </div>

                <Switch
                  checked={twoFactorEnabled}
                  onCheckedChange={handle2FAToggle}
                  disabled={twoFactorLoading}
                  data-testid="2fa-toggle"
                />
              </div>

              {/* 2FA Setup Modal */}
              {showSetup && (
                <div className="mt-6 p-6 bg-zinc-950 border border-emerald-500/20 rounded-lg space-y-4">
                  <h3 className="text-lg font-semibold text-emerald-400">2FA einrichten</h3>

                  {!backupCodes.length ? (
                    <>
                      <div className="space-y-3">
                        <p className="text-sm text-zinc-400">
                          1. Scanne diesen QR-Code mit deiner Authenticator-App:
                        </p>
                        {qrCode && (
                          <div className="flex justify-center bg-white p-3 md:p-4 rounded">
                            <img src={qrCode} alt="QR Code" className="w-40 h-40 md:w-48 md:h-48" />
                          </div>
                        )}

                        <p className="text-sm text-zinc-400">
                          2. Oder gib diesen Code manuell ein:
                        </p>
                        <div className="bg-zinc-800 p-2 md:p-3 rounded overflow-x-auto">
                          <code className="block text-center text-emerald-400 font-mono text-[9px] sm:text-[10px] md:text-sm whitespace-nowrap">
                            {secret}
                          </code>
                        </div>

                        <p className="text-sm text-zinc-400">
                          3. Gib den 6-stelligen Code aus der App ein:
                        </p>
                        <Input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={verifyCode}
                          onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                          className="bg-zinc-900 border-zinc-700 text-center text-lg tracking-widest"
                          placeholder="000000"
                          data-testid="2fa-verify-input"
                        />

                        <Button
                          onClick={verify2FASetup}
                          disabled={twoFactorLoading || verifyCode.length !== 6}
                          className="w-full bg-emerald-600 hover:bg-emerald-700"
                          data-testid="verify-2fa-button"
                        >
                          {twoFactorLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Verifiziere...
                            </>
                          ) : (
                            'Code bestätigen'
                          )}
                        </Button>
                        
                        <Button
                          onClick={() => {
                            setShowSetup(false);
                            setQrCode('');
                            setSecret('');
                            setVerifyCode('');
                          }}
                          variant="outline"
                          className="w-full border-zinc-700 hover:bg-zinc-800"
                        >
                          Abbrechen
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-emerald-500">
                        <Check className="w-5 h-5" />
                        <p className="font-semibold">2FA erfolgreich aktiviert!</p>
                      </div>

                      <Alert className="bg-orange-950/30 border-orange-500/50">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          <p className="font-semibold mb-2">Backup-Codes (Bitte sicher speichern!):</p>
                          <div className="grid grid-cols-2 gap-2">
                            {backupCodes.map((code, i) => (
                              <code key={i} className="bg-zinc-800 p-2 rounded text-xs font-mono">
                                {code}
                              </code>
                            ))}
                          </div>
                          <p className="text-xs mt-2">
                            Diese Codes können nur einmal verwendet werden, falls du keinen Zugriff auf deine Authenticator-App hast.
                          </p>
                        </AlertDescription>
                      </Alert>
                      
                      <Button
                        onClick={() => {
                          setShowSetup(false);
                          setVerifyCode('');
                          setQrCode('');
                          setSecret('');
                          setBackupCodes([]);
                          loadUserData();
                        }}
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        data-testid="backup-codes-confirmed"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Ich habe die Backup-Codes gespeichert
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* 3. E-MAIL-LOGIN-CODE */}
          <Card className="bg-zinc-900 border-zinc-800" data-testid="email-code-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-500" />
                E-Mail-Code beim Login
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Verlange zusätzlich einen Code per E-Mail bei jedem Login
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">
                    Status: {emailCodeEnabled ? (
                      <span className="text-emerald-500">An</span>
                    ) : (
                      <span className="text-zinc-400">Aus</span>
                    )}
                  </p>
                  <p className="text-sm text-zinc-500">
                    {emailCodeEnabled 
                      ? 'Du erhältst bei jedem Login einen Code per E-Mail' 
                      : 'Aktiviere für zusätzliche Login-Sicherheit'}
                  </p>
                </div>

                <Switch
                  checked={emailCodeEnabled}
                  onCheckedChange={handleEmailCodeToggle}
                  disabled={emailCodeLoading}
                  data-testid="email-code-toggle"
                />
              </div>

              <Alert className="mt-4 bg-blue-950/30 border-blue-500/50">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  <strong>Hinweis:</strong> Dies ist unabhängig vom Admin-Panel. 
                  Der Admin-Zugriff erfordert immer einen separaten Step-Up-Code.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Warnung */}
          {user?.email === 'info@snapshotpro.ai' && (
            <Alert className="bg-orange-950/30 border-orange-500/50">
              <Shield className="h-4 w-4" />
              <AlertDescription>
                <strong>Admin-Hinweis:</strong> Diese Einstellungen gelten nur für den Login. 
                Der Zugriff auf das Admin-Panel erfordert IMMER einen zusätzlichen Step-Up-Code, 
                unabhängig von diesen Einstellungen.
              </AlertDescription>
            </Alert>
          )}
        </div>
        
        {/* 2FA Disable Dialog */}
        <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
          <DialogContent className="sm:max-w-md bg-zinc-950 border-red-500/20">
            <DialogHeader>
              <DialogTitle className="text-red-400">2FA deaktivieren</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Zur Sicherheit benötigen wir dein Passwort und einen 2FA-Code.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {disableError && (
                <Alert variant="destructive" className="bg-red-950/30 border-red-500/50">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{disableError}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="disable-password">Passwort</Label>
                <Input
                  id="disable-password"
                  type="password"
                  value={disableForm.password}
                  onChange={(e) => setDisableForm({...disableForm, password: e.target.value})}
                  className="bg-zinc-900 border-zinc-700"
                  data-testid="disable-2fa-password"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="disable-code">2FA-Code</Label>
                <Input
                  id="disable-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={disableForm.code}
                  onChange={(e) => setDisableForm({...disableForm, code: e.target.value.replace(/\D/g, '')})}
                  className="bg-zinc-900 border-zinc-700 text-center text-lg tracking-widest"
                  placeholder="000000"
                  data-testid="disable-2fa-code"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDisableDialog(false)}
                  className="flex-1 border-zinc-700"
                  disabled={twoFactorLoading}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={confirm2FADisable}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={twoFactorLoading || !disableForm.password || disableForm.code.length !== 6}
                  data-testid="confirm-disable-2fa"
                >
                  {twoFactorLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deaktiviere...
                    </>
                  ) : (
                    '2FA deaktivieren'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
